import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { RecordDialog } from '../components/RecordDialog';
import { UserPremiumDialog } from '../components/UserPremiumDialog';
import { UserEditDialog } from '../components/UserEditDialog';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';
import { formatDate, formatDateTime } from '../utils/date';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export function UsersPage() {
  const { t } = useAppUi();
  const { admin } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [search, setSearch] = React.useState('');

  const [jsonOpen, setJsonOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [premiumOpen, setPremiumOpen] = React.useState(false);
  const [savingPremium, setSavingPremium] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [savingEdit, setSavingEdit] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/users', {
        params: { take: pageSize, page: page + 1, search: search || undefined },
      });
      const data = unwrapApiResponse(res);
      const list = data?.users || data?.rows || data?.data || data?.items;
      setRows(Array.isArray(list) ? list : data?.users || []);
      setRowCount(data?.total || 0);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const canWrite = admin?.role === 'SUPER_ADMIN' || admin?.role === 'ADMIN';
  const isSuper = admin?.role === 'SUPER_ADMIN';

  const openJson = (user) => {
    setSelectedUser(user);
    setJsonOpen(true);
  };

  const openPremium = (user) => {
    setSelectedUser(user);
    setPremiumOpen(true);
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setEditOpen(true);
  };

  const openDelete = (user) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  function UserActionsCell({ row }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const close = () => setAnchorEl(null);

    const onAction = async (fn) => {
      close();
      await fn();
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          aria-label={t('users.colActions')}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={(e) => {
            e?.stopPropagation?.();
            close();
          }}
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.25,
                minWidth: 200,
              },
            },
          }}
        >
          <MenuItem onClick={() => onAction(() => openJson(row))}>{t('common.view')}</MenuItem>
          <MenuItem disabled={!canWrite} onClick={() => onAction(() => openPremium(row))}>
            {t('common.premium')}
          </MenuItem>
          {isSuper ? (
            <>
              <MenuItem onClick={() => onAction(() => openEdit(row))}>{t('common.edit')}</MenuItem>
              <MenuItem
                onClick={() => onAction(() => openDelete(row))}
                sx={{ color: 'error.main' }}
              >
                {t('common.delete')}
              </MenuItem>
            </>
          ) : null}
        </Menu>
      </>
    );
  }

  const savePremium = async (patch) => {
    if (!selectedUser?.id) return;
    setSavingPremium(true);
    setError(null);
    try {
      await http.put(`/admin/users/${selectedUser.id}/premium`, patch);
      setPremiumOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSavingPremium(false);
    }
  };

  const saveEdit = async (patch) => {
    if (!selectedUser?.id) return;
    setSavingEdit(true);
    setError(null);
    try {
      await http.put(`/admin/users/${selectedUser.id}`, patch);
      setEditOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser?.id) return;
    setDeleting(true);
    setError(null);
    try {
      await http.delete(`/admin/users/${selectedUser.id}`);
      setDeleteOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setDeleting(false);
    }
  };

  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: t('users.colId'), width: 80 },
      { field: 'name', headerName: t('users.colName'), flex: 1, minWidth: 180 },
      { field: 'email', headerName: t('users.colEmail'), flex: 1, minWidth: 220 },
      { field: 'phone_number', headerName: t('users.colPhone'), width: 160 },
      { field: 'agency_name', headerName: t('users.colAgency'), flex: 1, minWidth: 180 },
      {
        field: 'expired_date',
        headerName: t('users.colPaidUntil'),
        width: 130,
        // expired_date is unix ms; only shown while the paid period is still running.
        valueGetter: (v, row) => (row.premium && Number(v) > Date.now() ? Number(v) : null),
        valueFormatter: (v) => (v ? formatDate(v) : '—'),
      },
      { field: 'created_at', headerName: t('users.colCreated'), width: 150, valueFormatter: (v) => formatDateTime(v) },
      {
        field: 'actions',
        headerName: t('users.colActions'),
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (p) => <UserActionsCell row={p.row} />,
      },
    ],
    [t, canWrite, isSuper]
  );

  return (
    <Box>
      <PageHeader
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        right={
          <Button variant="outlined" onClick={fetchData} disabled={loading}>
            {t('common.refresh')}
          </Button>
        }
      />

      <ErrorBanner error={error} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label={t('users.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', md: 420 } }}
        />
      </Stack>

      <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          density="compact"
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <RecordDialog
        open={jsonOpen}
        title={t('users.dialogTitle', { id: selectedUser?.id || '' })}
        record={selectedUser}
        onClose={() => setJsonOpen(false)}
      />

      <UserPremiumDialog
        open={premiumOpen}
        user={selectedUser}
        saving={savingPremium}
        onClose={() => setPremiumOpen(false)}
        onSave={savePremium}
      />

      <UserEditDialog
        open={editOpen}
        user={selectedUser}
        saving={savingEdit}
        onClose={() => setEditOpen(false)}
        onSave={saveEdit}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('users.deleteTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {t('users.deleteConfirm', { id: selectedUser?.id ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={deleteUser} disabled={deleting}>
            {deleting ? t('common.saving') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

